/**
 * Lista documenti commerciali (Fase 2.3): proforma, ricevute, scontrini, ecc.
 * Filtro per tipo (da lookup) + tab emesso/ricevuto. UI solo da @floraltech/ui.
 */
import { Button, Card, CardContent } from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';

interface DocumentoRiga {
  id: string;
  tipo: string;
  numero: string;
  direzione: string;
  stato: string;
  stato_pagamento: string;
  data_documento: string;
  controparte_denominazione: string | null;
  importo_totale: number | string;
}

interface Classificazione {
  id: string;
  codice: string;
  descrizione: string;
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

export function DocumentiList() {
  const navigate = useNavigate();
  const [documenti, setDocumenti] = useState<DocumentoRiga[]>([]);
  const [direzione, setDirezione] = useUrlState('direzione', 'ricevuto');
  const [tipo, setTipo] = useUrlState('tipo', '');
  const [tipi, setTipi] = useState<Classificazione[]>([]);

  useEffect(() => {
    void (async () => {
      const res = await fetch(
        '/api/v1/classificazioni?sistema=tipo_documento_commerciale',
        { headers: authHeaders() },
      );
      if (res.ok) setTipi(await res.json());
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      const params = new URLSearchParams({ direzione });
      if (tipo) params.set('tipo', tipo);
      const res = await fetch(`/api/v1/documenti-commerciali?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (res.ok) setDocumenti(await res.json());
      else setDocumenti([]);
    })();
  }, [direzione, tipo]);

  const descTipo = (codice: string) =>
    tipi.find((t) => t.codice === codice)?.descrizione ?? codice;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold">Documenti commerciali</h1>
        <Button size="sm" onClick={() => navigate('/documenti-commerciali/nuovo')}>
          Nuovo documento
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {['emesso', 'ricevuto'].map((d) => (
          <button
            key={d}
            className={
              'rounded px-3 py-1 text-sm ' +
              (direzione === d
                ? 'bg-neutral-800 text-white dark:bg-neutral-200 dark:text-neutral-900'
                : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')
            }
            onClick={() => setDirezione(d)}
          >
            {d === 'emesso' ? 'Emessi (a cliente)' : 'Ricevuti (da fornitore)'}
          </button>
        ))}
        <select
          className="ml-2 rounded border bg-transparent px-3 py-1 text-sm"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="">Tutti i tipi</option>
          {tipi.map((t) => (
            <option key={t.id} value={t.codice}>
              {t.descrizione}
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardContent className="pt-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2">Tipo</th>
                <th className="pb-2">Numero</th>
                <th className="pb-2">Data</th>
                <th className="pb-2">{direzione === 'emesso' ? 'Cliente' : 'Fornitore'}</th>
                <th className="pb-2">Pagamento</th>
                <th className="pb-2">Totale</th>
              </tr>
            </thead>
            <tbody>
              {documenti.map((d) => (
                <tr
                  key={d.id}
                  className="cursor-pointer border-t hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  onClick={() => navigate(`/documenti-commerciali/${d.id}`)}
                >
                  <td className="py-2">{descTipo(d.tipo)}</td>
                  <td className="py-2">{d.numero}</td>
                  <td className="py-2">{d.data_documento}</td>
                  <td className="py-2">{d.controparte_denominazione ?? '-'}</td>
                  <td className="py-2">{d.stato_pagamento}</td>
                  <td className="py-2">{fmtEUR.format(Number(d.importo_totale))}</td>
                </tr>
              ))}
              {documenti.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-muted-foreground">
                    Nessun documento {direzione}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default DocumentiList;
